import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Ticket, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang, STR, loc } from "@/i18n/translations";
import api from "@/lib/api";

export default function Events() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [remindEvent, setRemindEvent] = useState(null);
  const [remindEmail, setRemindEmail] = useState("");
  const [remindLoading, setRemindLoading] = useState(false);

  const submitReminder = async (ev) => {
    ev.preventDefault();
    if (!remindEvent) return;
    setRemindLoading(true);
    try {
      await api.post("/event-reminders", { event_id: remindEvent.id, email: remindEmail });
      toast.success(t({ fr: "Rappel activé ! Vérifiez vos emails.", en: "Reminder set! Check your inbox." }));
      setRemindEvent(null);
      setRemindEmail("");
    } catch {
      toast.error(t({ fr: "Erreur, réessayez.", en: "Error, try again." }));
    } finally {
      setRemindLoading(false);
    }
  };

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const filtered = events.filter((e) => (filter === "all" ? true : e.status === filter));

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.nav.events)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{t(STR.home.eventsTitle)}</h1>
        <div className="gold-line w-24 my-10" />

        <div className="flex gap-3 mb-10">
          {[["upcoming", STR.common.upcoming], ["past", STR.common.past], ["all", STR.common.all]].map(([k, label]) => (
            <button
              key={k} onClick={() => setFilter(k)} data-testid={`events-filter-${k}`}
              className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                filter === k ? "bg-gold text-obsidian border-gold" : "border-gold/25 text-cream/70 hover:border-gold"
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>

        <div className="grid gap-5">
          {filtered.map((e, i) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card-dark rounded-2xl overflow-hidden grid md:grid-cols-[240px_1fr] gap-0"
              data-testid={`event-${e.id}`}
            >
              <div className="relative h-48 md:h-full">
                <img src={e.image} alt={loc(e, "title", lang)} className="w-full h-full object-cover" />
                {e.is_tour && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-crimson-accent/90 text-white px-2.5 py-1 rounded">Sily Tour</span>
                )}
              </div>
              <div className="p-7 flex flex-col md:flex-row md:items-center justify-between gap-5">
                <div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${e.status === "upcoming" ? "text-emerald-accent border-emerald-accent/50 bg-emerald-accent/10" : "text-cream/40 border-white/10"}`}>
                    {e.status === "upcoming" ? t(STR.common.upcoming) : t(STR.common.past)}
                  </span>
                  <h2 className="font-serif text-2xl lg:text-3xl mt-2">{loc(e, "title", lang)}</h2>
                  <p className="text-gold mt-1">{loc(e, "artist", lang)}</p>
                  <p className="text-cream/60 text-sm mt-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {loc(e, "city", lang)} · {loc(e, "venue", lang)} · {e.date}
                  </p>
                </div>
                {e.status === "upcoming" && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/booking" data-testid={`event-ticket-${e.id}`} className="btn-outline-gold rounded-full px-6 py-3 text-sm flex items-center gap-2 whitespace-nowrap justify-center">
                      <Ticket className="w-4 h-4" /> {t(STR.common.tickets)}
                    </Link>
                    <button onClick={() => setRemindEvent(e)} data-testid={`event-remind-${e.id}`} className="btn-gold rounded-full px-6 py-3 text-sm flex items-center gap-2 whitespace-nowrap justify-center">
                      <Bell className="w-4 h-4" /> {t({ fr: "M'avertir", en: "Remind me" })}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          {filtered.length === 0 && <p className="text-cream/50 py-16 text-center">{t(STR.common.noDates)}</p>}
        </div>

        <Dialog open={!!remindEvent} onOpenChange={(o) => !o && setRemindEvent(null)}>
          <DialogContent className="bg-obsidian border border-gold/25 text-cream" data-testid="reminder-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-cream">
                {t({ fr: "Rappel concert", en: "Concert reminder" })}
              </DialogTitle>
            </DialogHeader>
            {remindEvent && (
              <form onSubmit={submitReminder} className="space-y-4">
                <p className="text-sm text-cream/70">
                  {t({ fr: "Recevez un email avant", en: "Get an email before" })}{" "}
                  <span className="text-gold">{loc(remindEvent, "title", lang)} — {loc(remindEvent, "city", lang)}</span> ({remindEvent.date}).
                </p>
                <input
                  type="email" required value={remindEmail} onChange={(ev) => setRemindEmail(ev.target.value)}
                  placeholder="Email" data-testid="reminder-email-input"
                  className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold"
                />
                <button type="submit" disabled={remindLoading} data-testid="reminder-submit-btn" className="btn-gold rounded-full w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                  {remindLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t({ fr: "Activer le rappel", en: "Set reminder" })}
                </button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
